/**
 * 分页 Feed 数据源 adapter 契约与共用工具。
 * Store 仍走 paginated-feed-core；仅 fetchPageResult / getQuery 因业务而异。
 */

import { FEED_FILTER_AUTO_PAGE_MAX } from '@/constants/feed-pagination.js';

/**
 * 标准 collection/page 响应 → hasMore 判定（Sport/Crypto/Home 共用）
 * @param {object} params
 * @param {object} params.data API 响应
 * @param {object[]} params.rows 当前页 rows（可与 data.rows 相同）
 * @param {number} params.pageSize
 * @param {number} params.rawRowsLength append 前已有条数
 * @param {boolean} params.append
 */
export function resolveFeedPaginationMeta({ data, rows, pageSize, rawRowsLength, append }) {
  const total = Number(data?.total ?? 0);
  const mergedLength = append ? rawRowsLength + rows.length : rows.length;

  if (rows.length < pageSize) {
    return { hasMore: false, mergedLength };
  }
  if (total > 0) {
    return { hasMore: mergedLength < total, mergedLength };
  }
  return { hasMore: true, mergedLength };
}

/**
 * 客户端过滤后不足 pageSize 时自动连拉后续页，直到填满目标或无更多数据。
 * @param {object} params
 * @param {number} params.pageSize 目标可展示条数（Sport=20 / Crypto=24）
 * @param {number} params.startPageNum 起始页码
 * @param {(pageNum: number) => Promise<{ data: object, rows: object[] }>} params.fetchPage
 * @param {(row: object) => boolean} params.filterRow
 * @param {number} [params.maxPages]
 */
export async function fetchFilteredPagesUntilTarget({
  pageSize,
  startPageNum,
  fetchPage,
  filterRow,
  maxPages = FEED_FILTER_AUTO_PAGE_MAX,
}) {
  const apiRows = [];
  let pageNum = startPageNum;
  let hasMore = false;
  let pagesFetched = 0;

  while (apiRows.length < pageSize && pagesFetched < maxPages) {
    const { data, rows } = await fetchPage(pageNum);
    const list = Array.isArray(rows) ? rows : [];
    for (const row of list) {
      if (filterRow(row)) apiRows.push(row);
    }
    pagesFetched += 1;

    const pageHasMore = resolveFeedPaginationMeta({
      data,
      rows: list,
      pageSize,
      rawRowsLength: 0,
      append: false,
    }).hasMore;

    if (!pageHasMore || list.length < pageSize) {
      hasMore = false;
      break;
    }

    hasMore = true;
    if (apiRows.length >= pageSize) break;
    pageNum += 1;
  }

  return {
    apiRows,
    nextPage: pageNum,
    hasMore,
  };
}

/**
 * @param {object} params
 * @param {() => object} params.buildQuery
 * @param {(ctx: object) => Promise<{ apiRows: object[], nextPage: number, hasMore: boolean }>} params.fetchPageResult
 * @param {string} [params.logTag]
 * @param {number} [params.pageSize]
 */
export function createFeedSourceAdapter({
  buildQuery,
  fetchPageResult,
  logTag = 'feed',
  pageSize,
}) {
  return {
    logTag,
    pageSize,
    getQuery: (store) => (typeof buildQuery === 'function' ? buildQuery(store) : {}),
    fetchPageResult,
  };
}
